import { useState } from 'react';
import { History } from 'lucide-react';
import { useAuditTrail } from '@my/tms-api/hooks';
import { cn, HistoryDialog } from '@my/ui';
import { resolveOrderReferenceLabel } from '@/utils/orderReferenceLabels';

interface OrderHistoryButtonProps {
  orderId: string | null;
  organisationId: string | null;
  /** Shown in the dialog title, so the reader knows which job they are looking at. */
  orderNumber: string | null;
  /** The org's reference labels, so `Reference1` reads as "Customer PO". */
  referenceLabels: string[];
}

/**
 * Names an entity the way the screen does. `OrderTaskCargoItem` is precise and
 * useless — the operator calls it cargo.
 */
function entityLabel(entityType: string): string {
  switch (entityType) {
    case 'Order':
      return 'Order';
    case 'OrderTask':
      return 'Task';
    case 'OrderTaskCargoItem':
      return 'Cargo';
    case 'OrderTaskResource':
      return 'Assignment';
    case 'OrderBooking':
      return 'Booking';
    case 'OrderChargeCost':
      return 'Charge / cost';
    case 'OrderDocument':
      return 'Document';
    case 'OrderServiceTag':
      return 'Service tag';
    default:
      return entityType;
  }
}

/**
 * Everything that happened to this order, whichever table it happened in — its
 * tasks, cargo rows, bookings, charges and documents all file under the job.
 *
 * <p>
 * Sits in the Actions row beside the move button: both are things you DO to the
 * order, and a completed order with no move left still has a history worth
 * opening. Nothing is fetched until it is.
 * </p>
 */
export function OrderHistoryButton({
  orderId,
  organisationId,
  orderNumber,
  referenceLabels,
}: OrderHistoryButtonProps) {
  const [open, setOpen] = useState(false);
  const query = useAuditTrail(organisationId, 'Order', orderId, { enabled: open });

  // An unsaved order has no history and cannot grow one until it exists.
  if (!orderId) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Order history"
        title="Who changed what, and when"
        onClick={() => setOpen(true)}
        className={cn(
          // Matches the advance button beside it: 28px, same radius, so the
          // Actions row reads as one set of controls.
          'inline-flex h-7 w-7 items-center justify-center rounded-md',
          'border border-slate-300 bg-white text-slate-500',
          'hover:bg-slate-50 hover:text-slate-700',
        )}
      >
        <History className="h-3.5 w-3.5" />
      </button>

      <HistoryDialog
        open={open}
        onClose={() => setOpen(false)}
        title={`History${orderNumber ? ` · ${orderNumber}` : ''}`}
        subtitle="who changed what, and when"
        entries={query.data ?? []}
        isLoading={query.isPending}
        error={query.isError ? 'Could not load this order history.' : null}
        hasMore={query.hasNextPage}
        isLoadingMore={query.isFetchingNextPage}
        onLoadMore={() => void query.fetchNextPage()}
        entityLabel={entityLabel}
        fieldLabel={(entityType, field) => {
          // The reference columns are numbered on the row and named on the
          // screen; "Reference1" would make the reader translate.
          const slot = /^Reference([1-9])$/.exec(field);
          if (entityType === 'Order' && slot) {
            return resolveOrderReferenceLabel(referenceLabels, Number(slot[1]) - 1);
          }
          return field;
        }}
        emptyMessage="Nothing recorded yet — the history starts from when auditing was switched on."
      />
    </>
  );
}
